'use client';

/**
 * Care Workflow Review Queue — the connected-agent experience for care workflows.
 *
 * Mirrors the Agents → Tickets model: a queue of workflow steps awaiting human
 * approval (clinician gates), each expandable into the full instance JOURNEY
 * (step timeline with decision telemetry + clinical provenance + event stream),
 * with inline Approve / Reject that resumes or halts the governed flow.
 *
 * This is where the clinical→social loop initiated by health/EHR signals becomes
 * visible and actionable, instead of firing bare referrals in the background.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Stethoscope,
  Radio,
  UserCheck,
  RefreshCw,
  Inbox,
} from 'lucide-react';
import { tcoWorkflowsAPI } from '@/services/api/tcoServiceReferrals';
import { useToast } from '@/components/ui/use-toast';

type GatedItem = {
  instance_id: number;
  step_number: number;
  step_name: string;
  step_type: string;
  member_id: number;
  template_id: number;
  template_name: string | null;
  trigger_type: string;
  trigger_source: Record<string, unknown> | null;
  started_at: string | null;
};

const STEP_ICON: Record<string, React.ReactNode> = {
  clinical_signal: <Stethoscope className="h-4 w-4" />,
  human_approval: <ShieldCheck className="h-4 w-4" />,
  create_referral: <Activity className="h-4 w-4" />,
  multi_channel_escalation: <Radio className="h-4 w-4" />,
  notify_pcp: <UserCheck className="h-4 w-4" />,
};

const ORIGIN_LABEL: Record<string, string> = {
  screen: 'AI health insight',
  diagnosis: 'EHR diagnosis',
  threshold: 'Observation threshold',
  discharge: 'Discharge',
  care_gap: 'Care gap',
};

function ProvenanceBadges({ source }: { source: Record<string, unknown> | null }) {
  if (!source) return null;
  const origin = (source.origin as string) || '';
  const icd = (source.icd_codes as string[]) || [];
  const risk = source.risk_score as number | undefined;
  const reasons = (source.reasons as string[]) || [];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {origin && (
        <Badge variant="secondary" className="gap-1">
          <Stethoscope className="h-3 w-3" />
          {ORIGIN_LABEL[origin] || origin}
        </Badge>
      )}
      {icd.slice(0, 4).map((c) => (
        <Badge key={c} variant="outline" className="font-mono text-[11px]">
          {c}
        </Badge>
      ))}
      {typeof risk === 'number' && risk > 0 && (
        <Badge variant={risk >= 70 ? 'destructive' : 'secondary'}>risk {risk}</Badge>
      )}
      {reasons.length > 0 && (
        <span className="text-xs text-muted-foreground">{reasons.join(' · ')}</span>
      )}
    </div>
  );
}

function JourneyView({ instanceId }: { instanceId: number }) {
  const [loading, setLoading] = useState(true);
  const [journey, setJourney] = useState<Awaited<ReturnType<typeof tcoWorkflowsAPI.getJourney>> | null>(null);

  useEffect(() => {
    let alive = true;
    tcoWorkflowsAPI
      .getJourney(instanceId)
      .then((j) => alive && setJourney(j))
      .catch(() => alive && setJourney(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [instanceId]);

  if (loading) return <div className="py-4 text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading journey…</div>;
  if (!journey) return <div className="py-4 text-sm text-muted-foreground">Journey unavailable.</div>;

  const stepStatusColor: Record<string, string> = {
    completed: 'text-green-600',
    awaiting_approval: 'text-amber-600',
    failed: 'text-red-600',
    skipped: 'text-muted-foreground',
    pending: 'text-muted-foreground',
  };

  return (
    <div className="space-y-4 border-l-2 border-muted pl-4">
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Steps</div>
        <ol className="space-y-1.5">
          {journey.steps.map((s) => {
            const status = String(s.status || '');
            const type = String(s.step_type || '');
            return (
              <li key={String(s.step_number)} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5">{STEP_ICON[type] || <ChevronRight className="h-4 w-4" />}</span>
                <div className="flex-1">
                  <span className="font-medium">{String(s.step_name)}</span>
                  <span className={`ml-2 text-xs ${stepStatusColor[status] || ''}`}>{status.replace('_', ' ')}</span>
                  {s.decision_reason && (
                    <div className="text-xs text-muted-foreground">{String(s.decision_reason)}</div>
                  )}
                  {s.approval_notes ? (
                    <div className="text-xs italic text-muted-foreground">“{String(s.approval_notes)}”</div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      {journey.events.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Event stream</div>
          <ul className="space-y-1">
            {journey.events.map((e, i) => (
              <li key={i} className="text-xs text-muted-foreground">
                <span className="font-mono">{e.event_type}</span> — {e.event_message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function CareWorkflowReviewQueue({ className }: { className?: string }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<GatedItem[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tcoWorkflowsAPI.getGated();
      setItems(res.gated || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (item: GatedItem, decision: 'approve' | 'reject') => {
    const key = `${item.instance_id}:${item.step_number}`;
    setActing(key);
    try {
      if (decision === 'approve') {
        await tcoWorkflowsAPI.approveStep(item.instance_id, item.step_number);
        toast({ title: 'Approved', description: 'The workflow has resumed.' });
      } else {
        await tcoWorkflowsAPI.rejectStep(item.instance_id, item.step_number);
        toast({ title: 'Rejected', description: 'The workflow was halted at this gate.' });
      }
      await load();
    } catch {
      toast({ title: 'Action failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setActing(null);
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Clinician review queue
          </CardTitle>
          <CardDescription>
            Clinical→social workflows initiated by health insights, EHR diagnoses, or observation
            thresholds — awaiting a clinician gate before referrals and member outreach proceed.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Loading review queue…
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Inbox className="mx-auto mb-2 h-6 w-6" />
            No workflows awaiting review.
          </div>
        ) : (
          items.map((item) => {
            const key = `${item.instance_id}:${item.step_number}`;
            const isOpen = expanded === item.instance_id;
            return (
              <div key={key} className="rounded-lg border">
                <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <button
                      type="button"
                      className="flex items-center gap-1 text-sm font-medium hover:underline"
                      onClick={() => setExpanded(isOpen ? null : item.instance_id)}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {item.template_name || 'Clinical→Social workflow'}
                      <span className="text-muted-foreground">· member #{item.member_id}</span>
                    </button>
                    <ProvenanceBadges source={item.trigger_source} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      disabled={acting === key}
                      onClick={() => act(item, 'reject')}
                    >
                      <XCircle className="mr-1 h-4 w-4" />
                      Reject
                    </Button>
                    <Button size="sm" disabled={acting === key} onClick={() => act(item, 'approve')}>
                      {acting === key ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-1 h-4 w-4" />}
                      Approve
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t bg-muted/30 p-3">
                    <JourneyView instanceId={item.instance_id} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export default CareWorkflowReviewQueue;
